import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {Store} from '@ngrx/store';
import * as fromApp from '../../store/app.reducer';
import {environment} from '../../../environments/environment';
import {addUserPublicToCache} from '../store/shared.actions';
import {User} from '../models/user.interface';
import {UserPublic} from '../models/userPublic.interface';
import {Mutex} from 'async-mutex';
// @ts-ignore
import firebase, {DocumentSnapshot} from 'firebase';
import {takeUntil} from 'rxjs/operators';
import {Wrapper} from '../models/wrapper.model';
import {DocumentData, QueryDocumentSnapshot, SnapshotOptions} from '@angular/fire/firestore';
import {RatingSummary} from '../models/ratingSummary.interface';
import {convertToUser, convertToUserPublic} from '../converters/modelConverters';
import {firestore} from '../../app.module';
import {selectUsersPublic} from '../store/shared.selectors';

@Injectable({
  providedIn: 'root',
})
export class UserService {

  userMutex = new Mutex();
  userPublicMutex = new Mutex();
  destroy$: Subject<null> = new Subject();
  usersPublicById: Map<string, UserPublic> = new Map<string, UserPublic>();
  usersPublic$ = this.store.select(selectUsersPublic).pipe(takeUntil(this.destroy$));

  constructor(
      private store: Store<fromApp.AppState>) {
    // Note: ngOnInit is not called in a service class
    this.init();
  }

  init(): void {
    this.usersPublic$.subscribe(usersPublic => {
      if (usersPublic) {
        this.usersPublicById = new Map<string, UserPublic>();
        usersPublic.forEach(userPublic => {
          if (userPublic.uid)
            this.usersPublicById.set(userPublic.uid, userPublic);
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
  }

  async fetchUserPublic(uid: string, maxAgeInSec: number = environment.defaultUserPublicCacheAgeInSec): Promise<Wrapper<UserPublic>> {
    return this.userPublicMutex.runExclusive(async () => {
      const userPublicFromCache = this.usersPublicById.get(uid);
      if (userPublicFromCache !== undefined && this.isUserUpToDate(userPublicFromCache, maxAgeInSec))
        return new Wrapper<UserPublic>(userPublicFromCache);

      try {
        const userPublicDocSnapshot = await firestore.collection(environment.firestoreCollectionUsersPublic).doc(uid).withConverter(userPublicConverter).get();
        const userPublic = userPublicDocSnapshot.data();
        if (userPublic) {
          const userPublicWithIdAndCacheDate: UserPublic = {...userPublic, uid: userPublicDocSnapshot.id, cacheDate: new Date()};
          this.addUserPublicToCache(userPublicWithIdAndCacheDate);
          return new Wrapper<UserPublic>(userPublicWithIdAndCacheDate);
        }
      } catch (e: any) {
        return new Wrapper<UserPublic>(undefined, e.message);
      }

      // If no user was found
      return new Wrapper<UserPublic>(undefined);

    });
  }


  async fetchUsersPublic(startAfter?: DocumentSnapshot, limit: number = environment.defaultLoadUserUserPublicCount): Promise<Wrapper<UserPublic[]>> {
    try {
      let query = firestore.collection(environment.firestoreCollectionUsersPublic)
          .orderBy('creationDate', 'desc')
          .withConverter(userPublicConverter);
      if (limit)
        query = query.limit(limit);
      if (startAfter)
        query = query.startAfter(startAfter);
      const userPublicQuerySnapshot = await query.get();
      const usersPublic: UserPublic[] = [];
      userPublicQuerySnapshot.forEach(userPublicDocSnapshot => {
        const userPublic: UserPublic | undefined = userPublicDocSnapshot.data();
        if (userPublic) {
          const userPublicWithId = {...userPublic, uid: userPublicDocSnapshot.id};
          usersPublic.push(userPublicWithId);
        }
      });
      const lastVisible = userPublicQuerySnapshot.docs[userPublicQuerySnapshot.docs.length - 1];

      return new Wrapper<UserPublic[]>(usersPublic, undefined, lastVisible);
    } catch (e: any) {
      if (e.code === 'permission-denied')
        return new Wrapper<UserPublic[]>(undefined, $localize`You are not allowed to view these users.`);
      return new Wrapper<UserPublic[]>(undefined, e.message);
    }
  }


  /**
   * Sends the given userPublic to the backend serPublicver. If it has an ID, an existing userPublic is updated, otherwise a new one is added.
   * @param uid ID of the userPublic to be sent
   * @param userPublic userPublic to be sent. Need to be provided, even if a fullUserPublic is given
   * @param fullUserPublic userPublic to be written to the cache. This userPublic is not sent to the firestore
   * @param merge if true, only the fields given in the userPublic object will be updated. Otherwise, the whole userPublic object will be overwritten
   * @param onSuccessCallback callback to be called on success
   * @param onErrorCallback callback to be called on error
   */
  updateUserPublic(userPublic: UserPublic, fullUserPublic: UserPublic | undefined, merge: boolean, onSuccessCallback: () => void, onErrorCallback: (error: string) => void): void {
    firestore.collection(environment.firestoreCollectionUsersPublic).doc(userPublic.uid).set(userPublic, {merge}).then(
        () =>
            onSuccessCallback(),
        (error) =>
            onErrorCallback($localize`The userPublic could not be updated\: ${error}`),
    );
    if (merge && !fullUserPublic) {
      console.error('updateUserPublic called with a merge job without providing a fullUserPublic.');
      return;
    }
    this.addUserPublicToCache(merge && fullUserPublic ? fullUserPublic : userPublic);

  }

  calculateRatingSummary(user: UserPublic): RatingSummary {
    let overallScore: number | undefined;
    let overallCount = 0;
    let itemAsDescribedScore: number | undefined;
    let itemAsDescribedCount = 0;
    let itemReturnConditionScore: number | undefined;
    let itemReturnConditionCount = 0;
    let punctualityScore: number | undefined;
    let punctualityCount = 0;
    let friendlinessScore: number | undefined;
    let friendlinessCount = 0;
    const overall = user?.ratings?.overall;
    if (overall) {
      overallCount = overall.count1 + overall.count2 + overall.count3 + overall.count4 + overall.count5;
      overallScore = (overall.count1 + overall.count2 * 2 + overall.count3 * 3 + overall.count4 * 4 + overall.count5 * 5) / overallCount;
    }
    const itemAsDescribed = user?.ratings?.itemAsDescribed;
    if (itemAsDescribed) {
      itemAsDescribedCount = itemAsDescribed.count1 + itemAsDescribed.count2 + itemAsDescribed.count3 + itemAsDescribed.count4 + itemAsDescribed.count5;
      itemAsDescribedScore = (itemAsDescribed.count1 + itemAsDescribed.count2 * 2 + itemAsDescribed.count3 * 3 + itemAsDescribed.count4 * 4 + itemAsDescribed.count5 * 5) / itemAsDescribedCount;
    }
    const itemReturnCondition = user?.ratings?.itemReturnCondition;
    if (itemReturnCondition) {
      itemReturnConditionCount = itemReturnCondition.count1 + itemReturnCondition.count2 + itemReturnCondition.count3 + itemReturnCondition.count4 + itemReturnCondition.count5;
      itemReturnConditionScore = (itemReturnCondition.count1 + itemReturnCondition.count2 * 2 + itemReturnCondition.count3 * 3 + itemReturnCondition.count4 * 4 + itemReturnCondition.count5 * 5) / itemReturnConditionCount;
    }
    const punctuality = user?.ratings?.punctuality;
    if (punctuality) {
      punctualityCount = punctuality.count1 + punctuality.count2 + punctuality.count3 + punctuality.count4 + punctuality.count5;
      punctualityScore = (punctuality.count1 + punctuality.count2 * 2 + punctuality.count3 * 3 + punctuality.count4 * 4 + punctuality.count5 * 5) / punctualityCount;
    }
    const friendliness = user?.ratings?.friendliness;
    if (friendliness) {
      friendlinessCount = friendliness.count1 + friendliness.count2 + friendliness.count3 + friendliness.count4 + friendliness.count5;
      friendlinessScore = (friendliness.count1 + friendliness.count2 * 2 + friendliness.count3 * 3 + friendliness.count4 * 4 + friendliness.count5 * 5) / friendlinessCount;
    }
    return {
      itemReturnConditionCount,
      itemAsDescribedCount,
      itemAsDescribedScore,
      itemReturnConditionScore,
      friendlinessCount,
      friendlinessScore,
      overallScore,
      overallCount,
      punctualityCount,
      punctualityScore,
    };
  };

  /**
   * Checks, if the given user or userPublic is newer then the given max age.
   * @param userPublic user or userPublic to be checked
   * @param maxAgeInSec max age in seconds
   * @return true, if newer, false otherwise
   */
  private isUserUpToDate(user: UserPublic | User, maxAgeInSec: any): boolean {
    if (!user.cacheDate)
      return false;
    const cacheTime = user.cacheDate.getTime();
    const now = new Date().getTime();
    const ageInSec = (now - cacheTime) / 1000;
    return (ageInSec < maxAgeInSec);
  }

  private addUserPublicToCache(userPublic: UserPublic) {
    this.usersPublicById.set(userPublic.uid, userPublic);
    this.store.dispatch(addUserPublicToCache({userPublic}));
  }

}


// Firestore data converter
export const userPublicConverter = {
  toFirestore(userPublic: UserPublic): UserPublic {
    return userPublic;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot<DocumentData>, options: SnapshotOptions): UserPublic {
    return convertToUserPublic(snapshot.data(options));
  },
};

// Firestore data converter
export const userConverter = {
  toFirestore(user: User): User {
    return user;
  },
  fromFirestore(snapshot: QueryDocumentSnapshot<DocumentData>, options: SnapshotOptions): User {
    return convertToUser(snapshot.data(options));
  },
};

